import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Stack, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { RevenueByMonthReport } from '../../../types/reporting';
import { reportingService } from '../../../services/reportingService';

interface RevenueByMonthChartProps {
  data?: RevenueByMonthReport;
  loading?: boolean;
}

const RevenueByMonthChart: React.FC<RevenueByMonthChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const months = data?.months ?? [];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Revenue by Month</Typography>
          {data && (
            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                label={`Total: ${reportingService.formatCurrency(data.totalRevenue)}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`Avg: ${reportingService.formatCurrency(data.averageMonthlyRevenue)}`}
                variant="outlined"
              />
            </Stack>
          )}
        </Stack>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={months} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="monthLabel"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                tickFormatter={(value) => reportingService.formatCurrency(Number(value))}
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
                width={80}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 4,
                }}
                labelStyle={{ color: theme.palette.text.primary }}
                formatter={(value: number, name: string) =>
                  name === 'Revenue' ? reportingService.formatCurrency(value) : value
                }
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RevenueByMonthChart;
